export type ExifCategory = 'gps' | 'device' | 'datetime' | 'software' | 'other';

export type ExifField = {
  key: string;
  label: string;
  category: ExifCategory;
  display: string;
  raw: unknown;
};

export type ExifResult = {
  fields: ExifField[];
  gps?: { lat: number; lng: number };
};

const CATEGORY_BY_KEY: Record<string, ExifCategory> = {
  GPSLatitude: 'gps',
  GPSLongitude: 'gps',
  GPSLatitudeRef: 'gps',
  GPSLongitudeRef: 'gps',
  GPSAltitude: 'gps',
  GPSAltitudeRef: 'gps',
  GPSDateStamp: 'gps',
  GPSTimeStamp: 'gps',
  latitude: 'gps',
  longitude: 'gps',
  Make: 'device',
  Model: 'device',
  LensModel: 'device',
  LensMake: 'device',
  HostComputer: 'device',
  SerialNumber: 'device',
  BodySerialNumber: 'device',
  DateTimeOriginal: 'datetime',
  DateTime: 'datetime',
  DateTimeDigitized: 'datetime',
  CreateDate: 'datetime',
  ModifyDate: 'datetime',
  OffsetTime: 'datetime',
  OffsetTimeOriginal: 'datetime',
  Software: 'software',
  ProcessingSoftware: 'software',
  Artist: 'software',
  Copyright: 'software',
  HostComputer_System: 'software',
};

const LABEL_BY_KEY: Record<string, string> = {
  GPSLatitude: 'GPS latitude',
  GPSLongitude: 'GPS longitude',
  GPSAltitude: 'GPS altitude',
  GPSDateStamp: 'GPS date',
  GPSTimeStamp: 'GPS time',
  latitude: 'GPS latitude',
  longitude: 'GPS longitude',
  Make: 'camera make',
  Model: 'camera model',
  LensMake: 'lens make',
  LensModel: 'lens model',
  SerialNumber: 'serial number',
  BodySerialNumber: 'body serial number',
  HostComputer: 'host computer',
  DateTimeOriginal: 'date taken',
  DateTime: 'date',
  DateTimeDigitized: 'date digitized',
  CreateDate: 'create date',
  ModifyDate: 'modify date',
  Software: 'software',
  ProcessingSoftware: 'processing software',
  Artist: 'artist',
  Copyright: 'copyright',
};

const stringify = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toFixed(6);
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(stringify).join(', ');
  if (value instanceof Uint8Array) return `<binary ${String(value.byteLength)} bytes>`;
  try {
    return JSON.stringify(value);
  } catch {
    return '[unprintable]';
  }
};

const fieldFor = (key: string, raw: unknown): ExifField => {
  const category = CATEGORY_BY_KEY[key] ?? 'other';
  const label = LABEL_BY_KEY[key] ?? key;
  return { key, label, category, display: stringify(raw), raw };
};

/** Keys that we never expose in the review (internal exifr derivations, large blobs, etc.) */
const SKIP_KEYS = new Set([
  'thumbnail',
  'MakerNote',
  'UserComment',
  'PreviewImage',
  'ApplicationNotes',
  'ExifVersion',
  'FlashpixVersion',
  'ComponentsConfiguration',
  'ImageDescription',
  'YCbCrPositioning',
  'ResolutionUnit',
  'XResolution',
  'YResolution',
  'PixelXDimension',
  'PixelYDimension',
  'ExifImageWidth',
  'ExifImageHeight',
  'ColorSpace',
  'Compression',
  'BitsPerSample',
  'PhotometricInterpretation',
  'SamplesPerPixel',
  'RowsPerStrip',
  'StripByteCounts',
  'StripOffsets',
]);

type ParseResult = Record<string, unknown> | null;

const parseExifr = async (input: ArrayBuffer | Blob | File): Promise<ParseResult> => {
  const exifr = (await import('exifr')) as unknown as {
    parse: (
      input: ArrayBuffer | Blob | File,
      options?: { gps?: boolean; mergeOutput?: boolean; translateValues?: boolean },
    ) => Promise<ParseResult>;
  };
  try {
    return await exifr.parse(input, {
      gps: true,
      mergeOutput: true,
      translateValues: true,
    });
  } catch {
    return null;
  }
};

export const detectExif = async (input: ArrayBuffer | Blob | File): Promise<ExifResult> => {
  const parsed = await parseExifr(input);
  if (!parsed) return { fields: [] };

  const fields: ExifField[] = [];
  for (const [key, value] of Object.entries(parsed)) {
    if (SKIP_KEYS.has(key)) continue;
    if (value === null || value === undefined) continue;
    fields.push(fieldFor(key, value));
  }

  const lat = typeof parsed.latitude === 'number' ? parsed.latitude : undefined;
  const lng = typeof parsed.longitude === 'number' ? parsed.longitude : undefined;
  const result: ExifResult = { fields };
  if (
    lat !== undefined &&
    lng !== undefined &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  ) {
    result.gps = { lat, lng };
  }
  return result;
};
